<template>
    <div class="w-100 row g-0 mb-2">
        <div v-if="isFacility" class="col-12 col-lg-4 pe-lg-2 form-group d-flex flex-column">
            <label for="facility-name"> Facility Name </label>
            <TypeaheadInput
                id="facility-name"
                :getData="getVendorList"
                :value="selectedFacility"
                v-on:valueChanged="updateFacility"
                :init-value="selectedFacility"
                placeholder="Start Typing..."
            ></TypeaheadInput>
        </div>
        <div class="col-12 col-lg-4 pe-lg-2 form-group">
            <label for="country">
                Country
                <span class="text-danger" v-if="isRequired">*</span>
            </label>
            <CountryTypeahead
                id="country"
                :updateSelectedCountry="updateSelectedCountry"
                :prefill-country="address.country"
                :disabled="isFacilitySelected"
                v-on:blurredInput="v$.address.country.$touch()"
                @change="(e: any) => handleAllInputChange('country', address.country)"
            >
            </CountryTypeahead>
            <span v-if="v$.address.country.$error" class="field-invalid">Country is required.</span>
        </div>
        <div class="w-100"></div>
        <div class="col-12 col-lg-4 pe-lg-2 form-group">
            <label for="referral-address-line-1">
                Address Line 1
                <span class="text-danger" v-if="isRequired">*</span>
            </label>
            <PlacesAutocomplete
                id="referral-address-line-1"
                v-model:street="address.addressLine1"
                v-model:city="address.city"
                v-model:state="address.state"
                v-model:zip="address.zipCode"
                v-model:county="address.county"
                v-model:latitude="address.latitude"
                v-model:longitude="address.longitude"
                v-model:disabled="isFacilitySelected"
                v-model:country="autoCompleteCountry"
                :places-changed-call-back="placesChangedCallBack"
                v-on:blurredInput="v$.address.addressLine1.$touch()"
                @change="(e: any) => handleAllInputChange('addressLine1', address.addressLine1)"
            >
            </PlacesAutocomplete>
            <span v-if="v$.address.addressLine1.$error" class="field-invalid">Address Line 1 is required.</span>
        </div>
        <div class="col-12 col-lg-4 pe-lg-2 form-group">
            <label for="referral-address-line-2"> Address Line 2 </label>
            <input
                class="form-control"
                maxlength="50"
                type="text"
                v-model="address.addressLine2"
                placeholder="Enter Address Line 2"
            />
        </div>
        <div class="w-100"></div>
        <div class="col-12 col-lg-2 pe-lg-2 form-group">
            <label for="referral-city">
                City
                <span class="text-danger" v-if="isRequired">*</span>
            </label>
            <input
                class="form-control"
                maxlength="50"
                type="text"
                v-model="address.city"
                placeholder="Enter City"
                :disabled="isFacilitySelected"
                @blur="v$.address.city.$touch()"
                @change="(e) => handleAllInputChange('city', address.city)"
            />
            <span v-if="v$.address.city.$error" class="field-invalid">City is required.</span>
        </div>
        <div class="col-12 col-lg-2 pe-lg-2 form-group">
            <label for="referral-state">
                {{ isUnitedStates ? "State" : "State / Province / Territory" }}
                <span class="text-danger" v-if="isRequired">*</span>
            </label>
            <StateTypeahead
                v-model:selected-state="selectedState"
                v-model:prefill-state="address.state"
                :disabled="isFacilitySelected"
                v-if="isUnitedStates"
                v-on:blurredInput="v$.address.state.$touch()"
                @change="(e: any) => handleAllInputChange('state', e.target.value)"
            >
            </StateTypeahead>
            <input
                v-else
                class="form-control"
                maxlength="50"
                type="text"
                v-model="address.state"
                placeholder="Enter State / Province / Territory"
                :disabled="isFacilitySelected"
                @blur="v$.address.state.$touch()"
                @change="(e) => handleAllInputChange('state', address.state)"
            />
            <span v-if="v$.address.state.$error" class="field-invalid">State is required.</span>
        </div>
        <div class="col-12 col-lg-2 pe-lg-2 form-group">
            <label for="referral-zip">
                {{ isUnitedStates ? "ZIP" : "Postal Code" }}
                <span class="text-danger" v-if="isRequired">*</span>
            </label>
            <input
                class="form-control"
                maxlength="10"
                type="text"
                number-only
                :value="formatZipCode(address.zipCode)"
                @change="(e) => handleZipcodeChange(e)"
                placeholder="Enter Zip"
                :disabled="isFacilitySelected"
                v-if="isUnitedStates"
                @blur="v$.address.zipCode.$touch()"
            />
            <input
                class="form-control"
                maxlength="50"
                type="text"
                v-model="address.zipCode"
                placeholder="Enter Postal Code"
                :disabled="isFacilitySelected"
                v-else
                @blur="v$.address.zipCode.$touch()"
                @change="(e) => handleAllInputChange('zipCode', address.zipCode)"
            />
            <span v-if="v$.address.zipCode.$error" class="field-invalid">ZIP is required.</span>
        </div>
        <div class="col-12 col-lg-2 pe-lg-2 form-group">
            <label for="referral-county">
                County
                <span class="text-danger" v-if="isUnitedStates && isRequired">*</span>
            </label>
            <input
                class="form-control"
                maxlength="50"
                type="text"
                v-model="address.county"
                placeholder="Enter County"
            />
        </div>
        <div class="form-group row col-12" v-if="showMoveDate">
            <div class="form-group col-12 col-lg-3 pe-lg-2">
                <label for="date-of-movein" id="Referral_Date_Of_Birth_Label"> Move-in Date </label>
                <DatePicker :options="datePickerOptions" :getDate="handleDateChange">
                    <div class="input-group date datepickerInput">
                        <input
                            name="moveInDate"
                            id="Movein_Date"
                            type="text"
                            class="form-control"
                            placeholder="MM/DD/YYYY"
                            :value="formatDateInput(address.moveInDate)"
                            autocomplete="off"
                            date-format-only
                            @change="onDateInputChange"
                        />
                        <div class="input-group-addon input-group-append">
                            <div class="input-group-text">
                                <i class="fa fa-calendar"></i>
                            </div>
                        </div>
                    </div>
                </DatePicker>
            </div>
            <div class="form-group col-12 col-lg-3 pe-lg-2">
                <label for="date-of-moveout" id="Referral_Date_Of_Birth_Label"> Move-out Date </label>
                <DatePicker :getDate="handleDateChange">
                    <div class="input-group date datepickerInput">
                        <input
                            name="moveOutDate"
                            id="Moveout_Date"
                            type="text"
                            class="form-control"
                            placeholder="MM/DD/YYYY"
                            :value="formatDateInput(address.moveOutDate)"
                            autocomplete="off"
                            date-format-only
                            @change="onDateInputChange"
                        />
                        <div class="input-group-addon input-group-append">
                            <div class="input-group-text">
                                <i class="fa fa-calendar"></i>
                            </div>
                        </div>
                    </div>
                </DatePicker>
            </div>
        </div>
    </div>
</template>

<script lang="ts" src="./AddressForm.ts"></script>
